/**
 * EntityRegistry<T> - Generic registry for flow entities
 *
 * Responsibilities:
 * - Store entity instances of type T extending FlowEntity
 * - Provide CRUD operations (register, get, update, delete, getAll)
 * - Notify listeners on changes (Observable pattern)
 * - Optimize position updates (no notifications to prevent re-renders)
 *
 * Usage:
 * ```typescript
 * const registry = new EntityRegistry<Screen>();
 *
 * registry.register(screen);
 * const unsubscribe = registry.subscribe(screenId, () => {
 *   console.log('Entity changed');
 * });
 * registry.update(screenId, { name: 'New Name' }); // Triggers notification
 * registry.updatePosition(screenId, { x: 100, y: 200 }); // NO notification (optimized)
 * ```
 *
 * Sprint: sprint-3
 * Task: T004.1
 * Requirement: FR-P2-002
 */

import type { FlowEntity } from '@modules/reactFlow/types/flow-canvas-types';
import type { XYPosition } from '@xyflow/react';

type ChangeListener = (entityId: string) => void;
const REGISTRY_CLEAR_EVENT_ID = '__registry_clear__';

/**
 * Generic registry for entities extending FlowEntity
 *
 * Extracted from ScreenRegistry to support any entity type (Screen, StrategyBlock, etc.)
 * Maintains Observable pattern while optimizing position updates.
 */
export class EntityRegistry<T extends FlowEntity> {
  private entities: Map<string, T> = new Map();
  private listeners: Map<string, Set<ChangeListener>> = new Map();
  private globalListeners: Set<ChangeListener> = new Set();

  constructor() {}

  /**
   * Register an entity
   *
   * Stores by entityId (semantic UUID). Firestore ID available via entity.id field.
   */
  public register(entity: T): void {
    if (!entity.entityId) {
      console.error('EntityRegistry: ❌ Cannot register entity without entityId', entity);
      return;
    }

    // Store by entityId (PRIMARY KEY)
    this.entities.set(entity.entityId, entity);
    this.notifyListeners(entity.entityId);
  }

  /**
   * Get entity by ID
   */
  public get(entityId: string): T | undefined {
    return this.entities.get(entityId);
  }


  /**
   * Check if entity exists
   */
  public has(entityId: string): boolean {
    return this.entities.has(entityId);
  }

  /**
   * Get all registered entities
   */
  public getAll(): T[] {
    return Array.from(this.entities.values());
  }

  /**
   * Update entity (partial update)
   *
   * NOTE: This method notifies all listeners. Use updatePosition() for position-only
   * updates that shouldn't trigger content re-renders.
   */
  public update(entityId: string, updates: Partial<T>): void {
    const entity = this.entities.get(entityId);

    if (!entity) {
      console.warn(`EntityRegistry: Cannot update non-existent entity: ${entityId}`);
      return;
    }

    // Merge updates
    const updatedEntity = { ...entity, ...updates };
    this.entities.set(entityId, updatedEntity);

    // Notify listeners
    this.notifyListeners(entityId);
  }

  /**
   * Update only entity position (optimized - no notifications)
   *
   * Use this for drag-and-drop position updates that shouldn't trigger
   * entity content re-renders. Only updates position in memory.
   *
   * CRITICAL: This is a performance optimization - position updates during drag
   * must NOT trigger re-renders.
   *
   * @param entityId - Entity ID
   * @param position - New position { x, y }
   */
  public updatePosition(entityId: string, position: XYPosition): void {
    const entity = this.entities.get(entityId);

    if (!entity) {
      console.warn(`EntityRegistry: Cannot update position for non-existent entity: ${entityId}`);
      return;
    }

    // Update only position, no notification
    entity.position = position;
  }

  /**
   * Delete entity
   */
  public delete(entityId: string): boolean {
    const deleted = this.entities.delete(entityId);

    if (deleted) {
      this.notifyListeners(entityId);

      // Clean up listeners for deleted entity
      this.listeners.delete(entityId);
    }

    return deleted;
  }

  /**
   * Clear all entities
   */
  public clear(): void {
    const hadEntities = this.entities.size > 0;
    this.entities.clear();
    this.listeners.clear();
    if (hadEntities) {
      this.notifyGlobalListeners(REGISTRY_CLEAR_EVENT_ID);
    }
  }

  /**
   * Subscribe to changes for specific entity
   *
   * @param entityId - Entity ID to watch
   * @param listener - Callback when entity changes
   * @returns Unsubscribe function
   */
  public subscribe(entityId: string, listener: ChangeListener): () => void {
    if (!this.listeners.has(entityId)) {
      this.listeners.set(entityId, new Set());
    }

    this.listeners.get(entityId)!.add(listener);

    // Return unsubscribe function
    return () => {
      this.listeners.get(entityId)?.delete(listener);
    };
  }

  /**
   * Subscribe to all entity changes
   *
   * @param listener - Callback when any entity changes
   * @returns Unsubscribe function
   */
  public subscribeGlobal(listener: ChangeListener): () => void {
    this.globalListeners.add(listener);

    return () => {
      this.globalListeners.delete(listener);
    };
  }

  /**
   * Notify listeners of entity change
   */
  private notifyListeners(entityId: string): void {
    // Notify entity-specific listeners
    this.listeners.get(entityId)?.forEach(listener => {
      try {
        listener(entityId);
      } catch (error) {
        console.error(`EntityRegistry: Listener error for ${entityId}:`, error);
      }
    });

    this.notifyGlobalListeners(entityId);
  }

  private notifyGlobalListeners(entityId: string): void {
    this.globalListeners.forEach(listener => {
      try {
        listener(entityId);
      } catch (error) {
        console.error(`EntityRegistry: Global listener error for ${entityId}:`, error);
      }
    });
  }
}
